// /cases - the open matters the viewer may see, grouped by practice area.
//
// Reads the live case list (GET /api/cases?status=open), the server's own
// scope: the firm for admin+; for everyone else the matters they are
// assigned to or on the team of, plus the unclaimed review queue. An empty
// answer is honest and says which. Empty groups are dropped so the rail
// counts match the visible groups without dead headers.

import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  OTHER_AREA,
  PRACTICE_AREA_ORDER,
  practiceAreaById,
  paStyle,
} from "../../data/practice-areas";
import { Skeleton, SkeletonGroup } from "../../components/chrome/Skeleton";
import { SepList, SEP } from "../../components/chrome/SepList";
import { FixtureNotice } from "../../components/chrome/FixtureNotice";
import { usePageTrail } from "../../components/chrome/trail";
import { DuplicatesBlock } from "../../components/index/DuplicatesBlock";
import { useMatterList } from "../../hooks/useMatterList";
import { serverPracticeAreaLabel } from "../../api/mock";
import { SEARCH_MIN_QUERY, SEARCH_DEBOUNCE_MS } from "../../hooks/useGlobalSearch";
import type { Matter } from "../../data/types";
import { clientNameLabel, matterKindLabel, stageLabel } from "../../lib/caseLabels";
import { useCurrentUser } from "../../components/auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";

const FIXTURE_NOTICE: Record<string, string> = {
  unreachable: "Could not reach the server - showing prototype data.",
};

function buildGroups(all: Matter[]) {
  const order = PRACTICE_AREA_ORDER;
  const grouped: Array<{ id: string; label: string; accent: string; matters: Matter[] }> = [];
  for (const paId of order) {
    const pa = practiceAreaById(paId);
    if (!pa) continue;
    const ms = all.filter((m) => m.practiceArea === paId);
    if (ms.length === 0) continue;
    grouped.push({ id: paId, label: pa.label, accent: pa.accent, matters: ms });
  }
  // Catch-all for matters whose practiceArea is outside the canonical order
  const known = new Set(order);
  const stragglers = all.filter((m) => !known.has(m.practiceArea as never));
  if (stragglers.length > 0) {
    grouped.push({
      id: OTHER_AREA.id,
      label: OTHER_AREA.label,
      accent: OTHER_AREA.accent,
      matters: stragglers,
    });
  }
  return grouped;
}

function rowSub(m: Matter): ReactNode[] {
  const parts: ReactNode[] = [matterKindLabel(m), stageLabel(m.stage)];
  if (m.matter?.venue) parts.push(m.matter.venue);
  if (m.needsAttention) parts.push("needs attention");
  if (m.needsReview) {
    parts.push(<span style={{ color: "var(--warn-600)", fontWeight: 600 }}>awaiting review</span>);
  }
  return parts;
}

export function CasesIndex() {
  const [params] = useSearchParams();
  // The rail's Review item lands here; the flag narrows the list to
  // intake-born matters nobody has taken into work.
  const reviewOnly = params.get("review") === "1";
  // The rail's Other row lands here: the server's "other" bucket has no
  // /practice page. The param is the server's bucket name; one the server
  // does not know is its 400, shown as the error card.
  const area = params.get("area") ?? undefined;
  const areaLabel = area ? serverPracticeAreaLabel(area) : null;
  const pageName = reviewOnly ? "Awaiting review" : (areaLabel ?? "Cases");
  usePageTrail(
    reviewOnly || areaLabel
      ? [{ label: "Cases", to: "/cases" }, { label: pageName }]
      : [{ label: pageName }],
  );
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q]);
  const search = debouncedQ.length >= SEARCH_MIN_QUERY ? debouncedQ : undefined;

  const { data, isPending, isError, isFetching, isPlaceholderData } = useMatterList(search, {
    needsReview: reviewOnly,
    area,
  });
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const page = data?.matters ?? [];
  // The plain list is the server's open caseload already; a search reaches
  // closed and archived matters on purpose, and those are not open work.
  const matters = page.filter((m) => m.status !== "closed");
  const groups = buildGroups(matters);
  const capped = (data?.total ?? page.length) > page.length;
  const notice = data?.fixtures ? FIXTURE_NOTICE[data.fixtures] : null;
  // An empty card makes a claim; under placeholder data the previous key's
  // answer stands in while this one loads, so no card until it lands.
  const empty = !isError && !isPlaceholderData && matters.length === 0;
  // A capped page cannot count, the server's total can; for the plain list
  // it is the number the rail badge and the home tile show. A search brings
  // closed rows the page hides, so it names the open share beside the hits.
  const shown = matters.length;
  const hidden = page.length - shown;
  const noun = reviewOnly ? "matters awaiting review" : search ? "matches" : "open matters";
  const one = reviewOnly ? "matter awaiting review" : search ? "match" : "open matter";
  const headCount =
    (capped
      ? `Showing the newest ${page.length} of ${data?.total} ${noun}`
      : `${page.length} ${page.length === 1 ? one : noun}`) + (hidden > 0 ? `, ${shown} open` : "");

  // isPending, not isLoading: offline the ask is parked without fetching,
  // and that is still no answer. Placeholder data counts as settled.
  if (isPending) {
    return (
      <main className="cc-main">
        <div className="cc-body">
          <SkeletonGroup label="Loading cases">
            <Skeleton width="30%" height={22} />
            <Skeleton width="100%" height={84} radius={10} />
            <Skeleton width="100%" height={84} radius={10} />
          </SkeletonGroup>
        </div>
      </main>
    );
  }

  return (
    <main className="cc-main">
      <div className="cc-body">
        <div className="cc-index-head">
          <span className="title">{pageName}</span>
          <span className="count">{headCount}</span>
          <span className="blurb">
            {reviewOnly
              ? "Born from intake - open one and take it into work."
              : area === "other"
                ? "Matters outside the configured practice areas."
                : "Grouped by practice area. Click any matter to open the workspace."}
          </span>
        </div>
        {(reviewOnly || area) && (
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            <Link to="/cases" className="cc-link-subtle">
              Show all cases
            </Link>
          </div>
        )}

        <input
          className="cc-field"
          aria-label="Search cases"
          placeholder="Search by name, phone, email, A-number, case number..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 420, marginBottom: 4 }}
        />
        <div style={{ fontSize: 11, color: "var(--muted)", minHeight: 16, marginBottom: 8 }}>
          {q.trim().length > 0 && q.trim().length < SEARCH_MIN_QUERY
            ? `Type at least ${SEARCH_MIN_QUERY} characters to search.`
            : isFetching && data
              ? "Searching..."
              : ""}
        </div>

        {notice && <FixtureNotice>{notice}</FixtureNotice>}

        {isAdmin && !reviewOnly && !search && !area && <DuplicatesBlock />}

        {isError && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--warn-600)" }}>
            {reviewOnly && !search
              ? "Could not reach the server - the review queue is unavailable."
              : area && !search
                ? "The list is unavailable - the server refused the ask or could not be reached."
                : "Could not reach the server - search is unavailable."}
          </div>
        )}

        {empty && !area && !reviewOnly && !search && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--muted)" }}>
            {isAdmin
              ? "No open matters in the firm database."
              : "No open matters are assigned to you."}
          </div>
        )}

        {empty && area && !reviewOnly && !search && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--muted)" }}>
            No open matters in this area.
          </div>
        )}

        {empty && reviewOnly && !search && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--muted)" }}>
            The review queue is empty - every intake-born matter has been taken into work.
          </div>
        )}

        {empty && search && (
          <div className="cc-card" style={{ padding: "10px 18px", color: "var(--muted)" }}>
            {page.length > 0
              ? "Only closed or archived matters match - narrow the search."
              : reviewOnly
                ? // The server ANDs both filters, so a claimed matter is a
                  // real client that simply left the queue - never "no match".
                  `No matters awaiting review match "${debouncedQ}" - it may already be taken.`
                : `No open matters match "${debouncedQ}".`}
          </div>
        )}

        {groups.map((g) => (
          <section key={g.id} aria-labelledby={`group-${g.id}`} style={paStyle(g.id)}>
            <div className="cc-index-group-head">
              <span className="dot" style={{ background: g.accent }} />
              <span className="label" id={`group-${g.id}`}>
                {g.label}
              </span>
              <span className="count">
                {SEP.trim()} {g.matters.length}
              </span>
            </div>
            <div className="cc-card">
              {g.matters.map((m) => {
                const pa = practiceAreaById(m.practiceArea);
                return (
                  <Link key={m.eID} to={`/matter/${m.eID}`} className="cc-index-row cc-row-lift">
                    <span
                      className="pa-dot lg"
                      style={{ background: pa?.accent ?? "var(--muted)" }}
                    />
                    <div>
                      <div className="row-title">{clientNameLabel(m)}</div>
                      <div className="row-sub">
                        <SepList parts={rowSub(m)} />
                      </div>
                    </div>
                    <div className="row-meta">
                      <span className="tnum-mono">{m.eID}</span>
                      <div className="row-meta-sub">opened {m.opened}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}

        <div className="cc-detail-footer">
          <span>
            {shown} matter{shown === 1 ? "" : "s"}
          </span>
          <span className="sep">{SEP.trim()}</span>
          <span>
            {groups.length} practice area{groups.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>
    </main>
  );
}
